/**
 * Share a deal with everyone on the team or with specific people on it,
 * change what they can do, or stop sharing. "Everyone on the team" carries
 * one access for all; "Specific people" picks teammates, each with their own
 * Can edit / Can view.
 */
import { Search, X } from "lucide-react";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { USER_FILES_UPDATED_EVENT } from "../../constants/events";
import { useToast } from "../../context/ToastContext";
import {
	type FolderShareAccess,
	type FolderShareAudience,
	type FolderShareInfo,
	type ShareFolderRequest,
	type TeamMember,
	getShareCandidates,
	shareFolder,
	unshareFolder,
} from "../../services/fileApi";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";
import ModalDialog from "../ModalDialog";
import OwnerAvatar from "./OwnerAvatar";

interface ShareFolderDialogProps {
	folderId: number;
	folderName: string;
	fileCount: number;
	subFolderCount: number;
	/** The share on this folder now; null when it is private. */
	current: FolderShareInfo | null;
	onClose: () => void;
}

/** A person picked in the dialog, with the access they will get. */
interface PickedPerson extends TeamMember {
	access: FolderShareAccess;
}

const TITLE_ID = "share-folder-title";

const ACCESS_LABEL: Record<FolderShareAccess, string> = {
	edit: "Can edit",
	view: "Can view",
};

const ACCESS_HINT: Record<FolderShareAccess, string> = {
	edit: "Teammates can open the files, add new ones, and use them in chats and any workflow.",
	view: "Teammates can open the files and use them in chats and any workflow, but not add or delete files.",
};

const PEOPLE_HINT =
	"Only the people you add can open it. Can edit adds files; Can view only opens and uses them.";

const plural = (count: number, word: string) =>
	`${count} ${word}${count === 1 ? "" : "s"}`;

const SELECT_CLASS =
	"h-8 rounded-lg border border-gray-200 bg-white py-0 pl-2 pr-8 text-[12.5px] text-text-strong focus:border-[#004C4B] focus:ring-[#004C4B] disabled:text-gray-300";

const AccessSelect: React.FC<{
	label: string;
	value: FolderShareAccess;
	disabled?: boolean;
	onChange: (access: FolderShareAccess) => void;
}> = ({ label, value, disabled, onChange }) => (
	<select
		aria-label={label}
		value={value}
		disabled={disabled}
		onChange={(e) => onChange(e.target.value as FolderShareAccess)}
		className={SELECT_CLASS}
	>
		{(Object.keys(ACCESS_LABEL) as FolderShareAccess[]).map((key) => (
			<option key={key} value={key}>
				{ACCESS_LABEL[key]}
			</option>
		))}
	</select>
);

/** Does *person* match what was typed in the picker? */
const matches = (person: TeamMember, query: string) => {
	const needle = query.trim().toLowerCase();
	return (
		!needle ||
		person.name.toLowerCase().includes(needle) ||
		person.email.toLowerCase().includes(needle)
	);
};

/** Same people with the same access, whatever the order? */
const samePeople = (a: PickedPerson[], b: PickedPerson[]) =>
	a.length === b.length &&
	a.every((person) =>
		b.some((other) => other.id === person.id && other.access === person.access),
	);

const ShareFolderDialog: React.FC<ShareFolderDialogProps> = ({
	folderId,
	folderName,
	fileCount,
	subFolderCount,
	current,
	onClose,
}) => {
	const { showToast } = useToast();
	const initialAudience: FolderShareAudience = current?.audience ?? "team";
	const initialPeople = useMemo<PickedPerson[]>(
		() =>
			current?.audience === "people"
				? (current.members ?? []).map(({ id, name, email, access }) => ({
						id,
						name,
						email,
						access,
					}))
				: [],
		[current],
	);
	const [audience, setAudience] = useState<FolderShareAudience>(initialAudience);
	const [access, setAccess] = useState<FolderShareAccess>(
		current && initialAudience === "team" ? current.access : "edit",
	);
	const [people, setPeople] = useState<PickedPerson[]>(initialPeople);
	const [team, setTeam] = useState<TeamMember[] | null>(null);
	const [teamError, setTeamError] = useState(false);
	const [query, setQuery] = useState("");
	const [pickerOpen, setPickerOpen] = useState(false);
	const [highlight, setHighlight] = useState(0);
	const [saving, setSaving] = useState(false);
	const cancelRef = useRef<HTMLButtonElement>(null);
	const listId = useId();

	// The teammates are only needed once "Specific people" is chosen.
	const wantTeam = audience === "people";
	useEffect(() => {
		if (!wantTeam || team !== null) return;
		let live = true;
		getShareCandidates(folderId)
			.then((members) => live && setTeam(members))
			.catch(() => {
				if (!live) return;
				setTeam([]);
				setTeamError(true);
			});
		return () => {
			live = false;
		};
	}, [wantTeam, team, folderId]);

	const candidates = useMemo(
		() =>
			(team ?? []).filter(
				(person) =>
					!people.some((picked) => picked.id === person.id) &&
					matches(person, query),
			),
		[team, people, query],
	);

	const addPerson = (person: TeamMember) => {
		setPeople((list) => [...list, { ...person, access: "edit" }]);
		setQuery("");
		setHighlight(0);
	};
	const removePerson = (id: string) =>
		setPeople((list) => list.filter((person) => person.id !== id));
	const setPersonAccess = (id: string, next: FolderShareAccess) =>
		setPeople((list) =>
			list.map((person) =>
				person.id === id ? { ...person, access: next } : person,
			),
		);

	const onPickerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setPickerOpen(true);
			setHighlight((index) => Math.min(index + 1, candidates.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlight((index) => Math.max(index - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			const person = candidates[highlight];
			if (pickerOpen && person) addPerson(person);
		} else if (e.key === "Escape" && pickerOpen) {
			// Close the list, not the whole dialog.
			e.nativeEvent.stopImmediatePropagation();
			e.stopPropagation();
			setPickerOpen(false);
		}
	};

	const run = async (
		work: () => Promise<unknown>,
		done: string,
		failed: string,
	) => {
		setSaving(true);
		try {
			await work();
			window.dispatchEvent(
				new CustomEvent(USER_FILES_UPDATED_EVENT, { detail: { folderId } }),
			);
			showToast(done, "success");
			onClose();
		} catch (error) {
			showToast(friendlyErrorMessage(error, failed), "error");
			setSaving(false);
		}
	};

	const request: ShareFolderRequest =
		audience === "team"
			? { audience: "team", access }
			: {
					audience: "people",
					members: people.map((person) => ({
						user_id: person.id,
						access: person.access,
					})),
				};
	const save = () =>
		run(
			() => shareFolder(folderId, request),
			audience === "team"
				? `${folderName} is shared with your team`
				: `${folderName} is shared with ${
						people.length === 1 ? people[0].name : `${people.length} people`
					}`,
			"Couldn't share this folder.",
		);
	const stop = () =>
		run(
			() => unshareFolder(folderId),
			`${folderName} is no longer shared`,
			"Couldn't stop sharing this folder.",
		);

	const contents = [
		plural(fileCount, "file"),
		subFolderCount ? plural(subFolderCount, "sub-folder") : null,
	]
		.filter(Boolean)
		.join(" and ");
	const unchanged =
		current !== null &&
		audience === initialAudience &&
		(audience === "team"
			? current.access === access
			: samePeople(people, initialPeople));
	const nobody = audience === "people" && people.length === 0;

	const optionClass = (selected: boolean) =>
		`rounded-[10px] border px-3.5 py-2.5 ${
			selected ? "border-[#004C4B] bg-[#f6fbfb]" : "border-gray-200"
		}`;

	return (
		<ModalDialog
			titleId={TITLE_ID}
			onClose={onClose}
			initialFocusRef={cancelRef}
		>
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h2 id={TITLE_ID} className="text-xl font-semibold text-text-base">
						Share the {folderName} folder
					</h2>
					<p className="mt-1 text-sm text-text-muted">
						{contents}. {audience === "team" ? ACCESS_HINT[access] : PEOPLE_HINT}
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="rounded-md p-1.5 text-text-muted hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					<X className="h-[18px] w-[18px]" aria-hidden="true" />
				</button>
			</div>

			<p className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
				Who can open it
			</p>
			<div role="radiogroup" aria-label="Who can open it" className="space-y-2">
				<div
					className={`flex flex-wrap items-center gap-3 ${optionClass(audience === "team")}`}
				>
					<label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
						<input
							type="radio"
							name="share-audience"
							checked={audience === "team"}
							onChange={() => setAudience("team")}
							className="h-[18px] w-[18px] border-gray-300 text-[#004C4B] focus:ring-[#004C4B]"
						/>
						<span className="text-sm font-semibold text-text-base">
							Everyone on the team
						</span>
					</label>
					<AccessSelect
						label="Access for the team"
						value={access}
						disabled={audience !== "team"}
						onChange={setAccess}
					/>
				</div>

				<div className={optionClass(audience === "people")}>
					<label className="flex cursor-pointer items-center gap-3">
						<input
							type="radio"
							name="share-audience"
							checked={audience === "people"}
							onChange={() => setAudience("people")}
							className="h-[18px] w-[18px] border-gray-300 text-[#004C4B] focus:ring-[#004C4B]"
						/>
						<span className="text-sm font-semibold text-text-base">
							Specific people
						</span>
					</label>

					{audience === "people" && (
						<div className="mt-3 pl-[30px]">
							<div className="relative">
								<label className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 text-text-muted focus-within:border-[#004C4B]">
									<Search className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
									<input
										type="text"
										role="combobox"
										aria-label="Add people by name or email"
										aria-expanded={pickerOpen && candidates.length > 0}
										aria-controls={listId}
										aria-autocomplete="list"
										aria-activedescendant={
											pickerOpen && candidates[highlight]
												? `${listId}-${candidates[highlight].id}`
												: undefined
										}
										placeholder={
											team === null
												? "Loading your team…"
												: "Add people by name or email"
										}
										value={query}
										onChange={(e) => {
											setQuery(e.target.value);
											setHighlight(0);
											setPickerOpen(true);
										}}
										onFocus={() => setPickerOpen(true)}
										onBlur={() => setPickerOpen(false)}
										onKeyDown={onPickerKeyDown}
										className="share-people-input h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-text-strong placeholder:text-text-muted focus:outline-none focus:ring-0"
									/>
								</label>
								{pickerOpen && candidates.length > 0 && (
									<ul
										id={listId}
										role="listbox"
										aria-label="Teammates"
										className="absolute left-0 right-0 z-10 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
									>
										{candidates.map((person, index) => (
											<li
												key={person.id}
												id={`${listId}-${person.id}`}
												role="option"
												aria-selected={index === highlight}
												// Keep focus in the input; pick on press.
												onMouseDown={(e) => {
													e.preventDefault();
													addPerson(person);
												}}
												onMouseEnter={() => setHighlight(index)}
												className={`flex cursor-pointer items-center gap-2.5 px-2.5 py-1.5 ${
													index === highlight ? "bg-[#f6fbfb]" : ""
												}`}
											>
												<OwnerAvatar name={person.name || person.email} size="md" decorative />
												<span className="min-w-0">
													<span className="block truncate text-[13px] font-medium text-text-base">
														{person.name || person.email}
													</span>
													<span className="block truncate text-xs text-text-muted">
														{person.email}
													</span>
												</span>
											</li>
										))}
									</ul>
								)}
							</div>
							{team !== null && team.length === 0 && (
								<p className="mt-2 text-xs text-text-muted">
									{teamError
										? "Couldn't load your team. Close and try again."
										: "No one else on your team has an account yet."}
								</p>
							)}

							{people.length > 0 ? (
								<ul aria-label="People with access" className="mt-2 divide-y divide-gray-100">
									{people.map((person) => {
										const name = person.name || person.email;
										return (
											<li key={person.id} className="flex items-center gap-2.5 py-2">
												<OwnerAvatar name={name} size="md" decorative />
												<span className="min-w-0 flex-1">
													<span className="block truncate text-[13px] font-medium text-text-base">
														{name}
													</span>
													<span className="block truncate text-xs text-text-muted">
														{person.email}
													</span>
												</span>
												<AccessSelect
													label={`Access for ${name}`}
													value={person.access}
													onChange={(next) => setPersonAccess(person.id, next)}
												/>
												<button
													type="button"
													aria-label={`Remove ${name}`}
													onClick={() => removePerson(person.id)}
													className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-text-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
												>
													<X className="h-4 w-4" aria-hidden="true" />
												</button>
											</li>
										);
									})}
								</ul>
							) : (
								<p className="mt-2 text-xs text-text-muted">No one added yet.</p>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="mt-6 flex flex-wrap items-center gap-2.5">
				<p className="mr-auto text-xs text-text-muted">
					Stop sharing any time. Files others added stay in the folder.
				</p>
				{current && (
					<button
						type="button"
						disabled={saving}
						onClick={stop}
						className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:text-gray-300"
					>
						Stop sharing
					</button>
				)}
				<button
					ref={cancelRef}
					type="button"
					onClick={onClose}
					className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
				>
					Cancel
				</button>
				<button
					type="button"
					disabled={saving || unchanged || nobody}
					title={nobody ? "Add at least one person" : undefined}
					onClick={save}
					className="rounded-md bg-[#004C4B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#003a3a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C4B] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{current ? "Save" : "Share folder"}
				</button>
			</div>
		</ModalDialog>
	);
};

export default ShareFolderDialog;
